// 직업 무기 3D(Blender 헤드리스 생성, tools/blender/hero/hero_weapons.py → tools/assets/hero-models/pack-weapons.mjs).
// 종류 × 외형 단계(T1~T3, shared/gear itemLookTier) GLB를 지연 로드해 정점 색 지오메트리로 캐시하고, 손 본 아래 소켓에 붙인다.
// 카엘 대검은 기존 경로(kael.ts, Tripo GLB) 그대로. 축: GLB +Y 위, 원점 = 쥐는 곳, 정면 +Z(Blender -Y).
import * as THREE from 'three/webgpu';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { assetUrl } from '../pwa/assetMap';
import { paletteVertexColors } from './weaponPalette';

export type WeaponKind = 'bow' | 'staff' | 'hammer' | 'shield' | 'holy';
export type WeaponTier = 1 | 2 | 3;

export function weaponAsset(kind: WeaponKind, tier: WeaponTier): string { return `weapons/wpn_${kind}_t${tier}.glb`; }

/**
 * 손 본 기준 쥐는 자세. pos = m(본 축: Y = 손가락 방향), rot = 도(XYZ, 본 축). 생성기 축(Blender Z 위)을 기준으로 정한 값이라
 * 붙일 때 GLB Y 위 → Z 위 보정(X +90°)을 뒤에 곱한다. 카엘 리그 대기·공격 자세에서 렌더로 고른 값(qa/hero-models-20261027/weapons/grip_*.png).
 */
export const WEAPON_GRIP: Readonly<Record<WeaponKind, { bone: 'RightHand' | 'LeftHand'; pos: readonly [number, number, number]; rot: readonly [number, number, number]; yUp?: boolean }>> = {
  staff: { bone: 'RightHand', pos: [0, 0.08, 0.02], rot: [0, 90, 0] },
  holy: { bone: 'RightHand', pos: [0, 0.08, 0.02], rot: [0, 90, 0] },
  hammer: { bone: 'RightHand', pos: [0, 0.08, 0.02], rot: [0, 90, 0] },
  shield: { bone: 'LeftHand', pos: [0, 0.06, 0.04], rot: [90, 0, 0] },
  // 장궁: lyraLook과 같은 소켓 회전(LYRA_BOW_ROT·LYRA_BOW_POS, three 활 공간 그대로 → yUp)
  bow: { bone: 'LeftHand', pos: [0, 0.06, 0.02], rot: [139.4, 1.6, 13.6], yUp: true },
};

/**
 * 장궁 소켓 값(왼손 본 기준)을 몸별로 둔다. kael = 카엘 몸 + 라이라 색조 경로·용병 궁수(기존 값 그대로),
 * lyra = 라이라 전용 몸(lyra_t1.glb, Mixamo 리그: LeftHand 본 축이 카엘 몸과 달라 같은 회전을 쓰면 활이 51° 기울고 화살이 뒤(125°)를 본다).
 * lyra 값은 tools/blender/hero/foot_audit.py와 같은 GLB(meshopt 해제)에서 bow_shot 0~0.2초·bow_sky 1.6~2.0초 프레임의 LeftHand·RightHand
 * 월드 자세를 뽑아, 활대(활 공간 +Y)가 월드 위 ≤3.3°·화살(+Z)이 영웅 정면 ≤7°(클립 자체의 조준선이 정면에서 8°)·시위 중앙(당김 끝)이 오른손에서 5~11cm가 되게 푼 값이다. carryRot는 대기 7표본·달리기 9표본에서 활대 기울기 ≤23°·활 끝 바닥 위 ≥11cm·다리 뼈 캡슐과 ≥9cm가 되게 푼 값(조준 회전 그대로면 활이 대기에서 58° 기울고 달릴 때 수평으로 골반을 가로지른다). pos는 손 본 기준 주먹(손 정점 무게중심)이고
 * (qa/l1029-avatar/body/bow-socket.md). ratio = 캐릭터 키 대비 활 길이, reach = 가득 당길 때 시위 중앙이 물러나는 길이(활 길이 비율):
 * 라이라 몸의 당김 팔 길이(손 사이 약 0.65m)에 시위가 닿으려면 카엘 몸 기본(0.62·0.3)보다 긴 장궁·깊은 당김이 필요하다.
 */
export interface BowSocket {
  /** 쏘는 자세(조준·발사)의 소켓 회전(도, XYZ, 손 본 기준) */
  readonly rot: readonly [number, number, number];
  /** 들고 있는 자세(대기·달리기·피격 등 쏘지 않을 때)의 소켓 회전. 없으면 rot 그대로. 쏘기 시작·끝에 setLyraAim이 둘 사이를 섞는다 */
  readonly carryRot?: readonly [number, number, number];
  readonly pos: readonly [number, number, number];
  readonly ratio: number;
  readonly reach: number;
}
export const BOW_SOCKET: Readonly<Record<'kael' | 'lyra', BowSocket>> = {
  kael: { rot: [139.4, 1.6, 13.6], pos: [0, 0.06, 0.02], ratio: 0.62, reach: 0.3 },
  lyra: { rot: [-99.2, -9.7, 160.2], carryRot: [-27.6, -34.2, 152.7], pos: [0, 0.16, 0.005], ratio: 0.78, reach: 0.53 },
};

/** 장궁 GLB 길이(m)와 활 끝(three 활 공간: 손잡이 원점, 활대 ±Y, 화살 쪽 +Z) — lyraLook buildBowGeometry와 같은 비율 */
export const BOW_GLB_LENGTH = 1.12;
export function bowTips(L: number): { top: THREE.Vector3; bottom: THREE.Vector3 } {
  return { top: new THREE.Vector3(0, L / 2, 0.035 * L), bottom: new THREE.Vector3(0, -L / 2, 0.035 * L) };
}

const cache = new Map<string, Promise<THREE.BufferGeometry>>();
let loader: GLTFLoader | null = null;

/** 무기 지오메트리(정점 색, m). 한 번만 읽고 실패하면 캐시를 비운다 */
export function loadWeaponGeometry(kind: WeaponKind, tier: WeaponTier): Promise<THREE.BufferGeometry> {
  const key = kind + tier;
  let p = cache.get(key);
  if (!p) {
    loader ??= new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    p = loader.loadAsync(assetUrl(weaponAsset(kind, tier))).then((gltf) => {
      let geo: THREE.BufferGeometry | null = null;
      gltf.scene.updateMatrixWorld(true);
      gltf.scene.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!geo && m.isMesh) geo = m.geometry.clone().applyMatrix4(m.matrixWorld);
      });
      if (!geo) throw new Error('무기 메시 없음: ' + key);
      const out = paletteVertexColors(geo);
      (geo as THREE.BufferGeometry).dispose();
      gltf.scene.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) { m.geometry.dispose(); (m.material as THREE.Material).dispose(); } });
      return out;
    });
    p.catch(() => cache.delete(key));
    cache.set(key, p);
  }
  return p;
}

const _s = new THREE.Vector3();
const Y_UP_TO_Z_UP = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI / 2);

/** 소켓 국소 변환(본 배율 상쇄 뒤): 위치 m, 회전 = 본 축 오일러 × (GLB Y 위 → Z 위) */
export function gripTransform(kind: WeaponKind, out: { position: THREE.Vector3; quaternion: THREE.Quaternion }): void {
  const g = WEAPON_GRIP[kind];
  out.position.set(g.pos[0], g.pos[1], g.pos[2]);
  const d = Math.PI / 180;
  out.quaternion.setFromEuler(new THREE.Euler(g.rot[0] * d, g.rot[1] * d, g.rot[2] * d, 'XYZ'));
  if (!g.yUp) out.quaternion.multiply(Y_UP_TO_Z_UP);
}

/**
 * 무기를 손 본에 붙인다(본의 월드 배율 상쇄 소켓 → 무기 메시). 붙인 소켓을 돌려준다(떼기: socket.removeFromParent()).
 * root는 updateMatrixWorld가 끝난 모델이어야 본 배율을 읽을 수 있다.
 */
export function attachWeapon(root: THREE.Object3D, kind: WeaponKind, geometry: THREE.BufferGeometry, material: THREE.Material): THREE.Object3D | null {
  const want = WEAPON_GRIP[kind].bone;
  let bone: THREE.Object3D | null = null;
  root.traverse((o) => { if (!bone && (o as THREE.Bone).isBone && o.name.endsWith(want)) bone = o; });
  if (!bone) return null;
  const b = bone as THREE.Object3D;
  b.updateWorldMatrix(true, false);
  b.getWorldScale(_s);
  const socket = new THREE.Group();
  socket.name = 'weapon-socket-' + kind;
  socket.scale.setScalar(1 / (_s.x || 1));
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'weapon-' + kind;
  mesh.castShadow = true;
  gripTransform(kind, mesh);
  socket.add(mesh);
  b.add(socket);
  return socket;
}
